import { useState } from 'react';
import { ProviderSearch, Provider } from '../ProviderSearch';
import { appointmentService } from '../../services/appointment';
import { toast } from '../../utils/toast';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import type { UnifiedEncounterItem } from './types';

interface ChangeProviderModalProps {
  item: UnifiedEncounterItem;
  onClose: () => void;
  onChanged: () => void;
}

/**
 * An admin hands an appointment to another provider (AMP-137). In a standalone practice providers
 * are the operators who record, so appointments booked to an admin before that rule can be given
 * to someone who can record them. The provider list only offers who may be the provider.
 */
export function ChangeProviderModal({ item, onClose, onChanged }: ChangeProviderModalProps) {
  const [provider, setProvider] = useState<Provider | null>(null);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!provider) return;
    try {
      setSaving(true);
      await appointmentService.updateAppointment(item.id, { providerId: provider.id });
      toast.success(`Appointment assigned to ${provider.name}`);
      onChanged();
      onClose();
    } catch (error) {
      // The API's own message (e.g. "Choose an active provider of your organization") is toasted
      // by the API client; keep the modal open so the admin can pick someone else.
      setSaving(false);
    }
  };

  return (
    // Radix dialog: focus moves into it, Tab stays inside, Escape and the backdrop close it, and
    // focus returns to the Change button. None of that while saving.
    <Dialog open onOpenChange={(open) => { if (!open && !saving) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Change provider</DialogTitle>
          <DialogDescription>
            {item.patientName}
            {item.providerName ? ` · now with ${item.providerName}` : ''}
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2">
          <ProviderSearch
            onSelectProvider={setProvider}
            label="New provider"
            placeholder="Search for the provider who will see the patient..."
            required
          />
        </div>
        <DialogFooter className="mt-4">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="btn-primary" onClick={save} disabled={!provider || saving}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
